import React, { useContext } from "react";
import { v4 as uuid } from "uuid";
import useLocalStorage from "../hooks/useLocalStorage";
import { clone } from "../utils/utilities";
import { useFrasario } from "./FrasarioProvider";
import { useTranslation } from "react-i18next";
import { useSpeech } from "./SpeechProvider";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { useBoatInfo } from "./BoatInfoProvider";

const ConversationsContext = React.createContext();

export function useConversations() {
  return useContext(ConversationsContext);
}

export function ConversationsProvider({ children }) {
  const [conversations, setConversations] = useLocalStorage(
    "conversations",
    {}
  );
  //const [isLoading, setIsLoading] = useState(true);
  const isLoading = false;
  const { parseMessage } = useFrasario();
  const { t } = useTranslation();
  const { speak } = useSpeech();
  const axiosPrivate = useAxiosPrivate();
  const { boatInfo } = useBoatInfo();

  /*useEffect(() => {
    if (socket == null || !socket.connected) return;

    //TODO
    socket.on("conversationList", (conversationList) => {
      setConversations(conversationList);
      setIsLoading(false);
    });

    //return () => socket.off("conversationList");
  }, [socket, setConversations, setIsLoading]);*/

  function addMessageToConversation({ mmsi, message, received }) {
    setConversations((prevConversations) => {
      const newMessage = {
        ...message,
        received,
        _id: uuid(),
      };
      if (
        prevConversations[mmsi] != null &&
        prevConversations[mmsi].messages != null
      ) {
        while (
          prevConversations[mmsi].messages.some((m) => m._id === newMessage._id)
        ) {
          newMessage._id = uuid();
        }
        prevConversations[mmsi].messages.push(newMessage);
      } else if (prevConversations[mmsi] == null) {
        prevConversations[mmsi] = { messages: [newMessage] };
      } else {
        prevConversations[mmsi].messages = [newMessage];
      }
      return prevConversations;
    });
  }

  function sendMessage(recipientMmsi, message) {
    console.log("sending message: ", message);
    try {
      axiosPrivate.post("/api/v1/messages/message", {
        sender: boatInfo.id != null ? boatInfo.id : boatInfo.mmsi,
        recipient: recipientMmsi,
        payload: JSON.stringify(message),
      });
      addMessageToConversation({
        mmsi: recipientMmsi,
        message,
        received: false,
      });
    } catch (err) {
      console.log("Error sending message", err);
    }
  }

  function addReceivedMessage(message, ship, selectedShipMmsi = null) {
    if (message != null) {
      try {
        // let senderMMSI =
        //   message.sender != null
        //     ? message.sender.id != null
        //       ? message.sender.id
        //       : message.sender.MMSI
        //     : null;
        let senderMMSI = message.sender;
        if (senderMMSI == null) return;
        message = JSON.parse(message.payload);
        message.received = true;
        setConversations((prev) => {
          const newConversations = clone(prev);
          message._id = uuid();
          if (
            newConversations[senderMMSI] != null &&
            newConversations[senderMMSI].messages != null
          ) {
            while (
              newConversations[senderMMSI].messages.some(
                (m) => m._id === message._id
              )
            ) {
              message._id = uuid();
            }
          } else if (newConversations[senderMMSI] == null) {
            newConversations[senderMMSI] = { messages: [] };
          } else {
            newConversations[senderMMSI].messages = [];
          }
          message.timestamp = Date.now();
          newConversations[senderMMSI].messages.push(clone(message));
          // Update last message timestamp
          newConversations[senderMMSI].lastMessageTimestamp = Date.now();
          newConversations[senderMMSI].unreadMessages =
            newConversations[senderMMSI].unreadMessages != null
              ? newConversations[senderMMSI].unreadMessages + 1
              : 1;
          if (selectedShipMmsi != null && senderMMSI === selectedShipMmsi) {
            newConversations[senderMMSI].unreadMessages = 0;
          }
          if (
            newConversations[senderMMSI].shipName == null &&
            ship != null &&
            ship.name != null
          ) {
            newConversations[senderMMSI].shipName = ship.name;
          }
          return newConversations;
        });
        //if (selectedShipMmsi != null) {
        let messageAfterParse = parseMessage(message, false, false, true);
        if (messageAfterParse == null) return;
        let messageToRead = t("new_message_received");
        if (senderMMSI === selectedShipMmsi) {
          messageToRead = messageAfterParse.text;
        } else {
          if (ship != null && ship.name != null) {
            messageToRead += ship.name + "... ";
          } else {
            messageToRead += senderMMSI + "... ";
          }
          messageToRead += messageAfterParse.text;
        }
        //setParsedMessage(messageToRead);
        speak(messageToRead, t("language"));
        //}
      } catch (e) {
        console.log(`Exception while trying to read received message`, e);
      }
    }
  }

  function resetUnreadMessages(mmsi) {
    setConversations((prev) => {
      const newConversations = clone(prev);
      if (
        newConversations[mmsi] != null &&
        newConversations[mmsi].unreadMessages != null &&
        newConversations[mmsi].unreadMessages !== 0
      ) {
        newConversations[mmsi].unreadMessages = 0;
        return newConversations;
      }
      return prev;
    });
  }

  function resetConversations() {
    setConversations({});
  }

  const value = {
    conversations,
    isLoading,
    sendMessage,
    addReceivedMessage,
    resetUnreadMessages,
    resetConversations,
  };

  return (
    <ConversationsContext.Provider value={value}>
      {children}
    </ConversationsContext.Provider>
  );
}
